import type { ProgramScoringRow } from '../../interfaces/CamProgramming.interface';
import { formatDurationCaption } from './sessionLogging';

export type ProgrammingStatus = 'pending' | 'in_progress' | 'done';

export function programmingStatus(
	record: Pick<
		ProgramScoringRow,
		'programming_started_at' | 'programming_completed_at'
	>
): ProgrammingStatus {
	if (record.programming_completed_at) {
		return 'done';
	}
	if (record.programming_started_at) {
		return 'in_progress';
	}
	return 'pending';
}

export const PROGRAMMING_STATUS_LABELS: Record<ProgrammingStatus, string> = {
	pending: 'Pending',
	in_progress: 'In progress',
	done: 'Done',
};

export type ProgrammingLogStatus = 'in_progress' | 'completed';

/** Derive the log/edit modal status from stored timestamps. */
export function programmingLogStatus(
	startedAt: string | null | undefined,
	completedAt: string | null | undefined
): ProgrammingLogStatus {
	if (completedAt) {
		return 'completed';
	}
	if (startedAt) {
		return 'in_progress';
	}
	return 'in_progress';
}

export const PROGRAMMING_LOG_STATUS_LABELS: Record<
	ProgrammingLogStatus,
	string
> = {
	in_progress: 'In progress',
	completed: 'Completed',
};

export const PROGRAMMING_STATUS_FILTERS = (
	Object.entries(PROGRAMMING_STATUS_LABELS) as [ProgrammingStatus, string][]
).map(([value, text]) => ({ text, value }));

type ProgrammingCaptionRecord = Pick<
	ProgramScoringRow,
	'programming_duration_minutes' | 'programming_started_at' | 'programming_completed_at'
>;

/** User-facing line: status and approximate minutes, not clock timestamps. */
export function formatProgrammingCaption(
	record: ProgrammingCaptionRecord,
	options?: { pendingLabel?: string }
): string | null {
	const status = programmingStatus(record);
	const caption = formatDurationCaption(
		status,
		record.programming_duration_minutes
	);
	if (caption) {
		return caption;
	}
	return options?.pendingLabel ?? null;
}
